'use client';

import React, { useMemo, useState } from 'react';

type Dept = 'recruiting' | 'sourcing' | 'ops' | 'account' | 'marketing' | 'analytics';
type Loc = 'remote' | 'moscow' | 'spb';

type Person = {
  id: number;
  name: string;
  role: string;
  dept: Dept;
  loc: Loc;
  photo: string;
  tags: string[];
  bio: string;
};

const DATA: Person[] = [
  { id: 1, name: 'Марха Каримова', role: 'Lead Recruiter', dept: 'recruiting', loc: 'remote', photo: 'https://static.tildacdn.com/tild6637-3833-4334-b235-373164326332/user-female-circle-f.png', tags: ['ATS', 'интервью', 'офферы'], bio: 'Ведёт массовый и офисный найм, эксперт по ATS и коммуникациям.' },
  { id: 2, name: 'Сулейман Ахмедов', role: 'Head of Accounts', dept: 'account', loc: 'moscow', photo: 'https://avatars.githubusercontent.com/u/1608890?v=4?s=400', tags: ['SLA', 'аккаунтинг', 'договора'], bio: 'Управляет портфелем клиентов, отчётностью и удовлетворённостью.' },
  { id: 3, name: 'Танзила Джамбекова', role: 'Project Manager', dept: 'ops', loc: 'spb', photo: 'https://static.tildacdn.com/tild6637-3833-4334-b235-373164326332/user-female-circle-f.png', tags: ['KPI', 'процессы', 'BI'], bio: 'Собирает метрики в BI, держит сроки и помогает командам.' },
];

const Card: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className }) => (
  <article className={`rounded-2xl p-6 ${className || ''}`} style={{ background: 'var(--card)', backdropFilter: 'blur(8px)' }}>{children}</article>
);
const Chip: React.FC<React.PropsWithChildren<{ href: string }>> = ({ href, children }) => (
  <a href={href} className="px-3 py-1 rounded-full hover:bg-white/25 text-sm" style={{ background: 'var(--chip)' }}>{children}</a>
);

export default function TeamPage() {
  const [q, setQ] = useState('');
  const [dept, setDept] = useState<'' | Dept>('');
  const [loc, setLoc] = useState<'' | Loc>('');
  const [sort, setSort] = useState<'name' | 'dept'>('name');

  const items = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let arr = DATA.filter(p => {
      const hay = (p.name + ' ' + p.role + ' ' + p.tags.join(' ') + ' ' + p.bio).toLowerCase();
      const okQ = !ql || hay.includes(ql);
      const okD = !dept || p.dept === dept;
      const okL = !loc || p.loc === loc;
      return okQ && okD && okL;
    });
    arr.sort((a, b) => {
      if (sort === 'dept') return a.dept.localeCompare(b.dept) || a.name.localeCompare(b.name);
      return a.name.localeCompare(b.name);
    });
    return arr;
  }, [q, dept, loc, sort]);

  const labelLoc = (l: Loc) => l === 'moscow' ? 'Москва' : l === 'spb' ? 'Санкт-Петербург' : 'Remote';
  const labelDept = (d: Dept) => ({ recruiting: 'Рекрутинг', sourcing: 'Сорсинг', ops: 'Операции', account: 'Аккаунт/PM', marketing: 'Маркетинг', analytics: 'Аналитика' }[d]);

  return (
    <>
      <style>{`
        :root{ --accent:#FFC300; --card:rgba(255,255,255,.08); --chip:rgba(255,255,255,.14); }
        .btn{border-radius:.75rem; padding:.7rem 1rem; font-weight:700}
        .btn-accent{background:#FFC300; color:#102a43}
        .btn-ghost{background:white; color:#003B9B; border:2px solid #003B9B}
        .tag{background:rgba(255,255,255,.12); padding:.25rem .6rem; border-radius:999px; font-size:.75rem}
      `}</style>
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100">
        <header
          className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
          style={{
            background:
              'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)',
          }}
        >
          <div className="max-w-7xl mx-auto">
            <nav className="mb-6 flex flex-wrap gap-3 text-sm">
              <Chip href="#leaders">Лидеры</Chip>
              <Chip href="#team">Вся команда</Chip>
              <Chip href="#culture">Культура</Chip>
              <Chip href="#careers">Карьера</Chip>
            </nav>
            <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Команда INTERIM</h1>
            <p className="max-w-3xl text-lg md:text-xl text-gray-200">
              Мы — практики найма и сервисной работы. Прозрачные соглашения об уровне обслуживания (SLA), ответственность за результат и уважение к людям.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#team" className="px-[15px] py-[11px] rounded-xl font-semibold border border-blue-600 text-blue-600 bg-white">Смотреть команду</a>
              <a href="#careers" className="px-4 py-3 rounded-xl font-semibold" style={{ background: '#FFC300', color: '#102a43' }}>Присоединиться</a>
            </div>
          </div>
        </header>
        <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
          <section id="leaders">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Лидершип-команда</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {[
                ['Амина Амирова', 'CEO • Стратегия и партнёрства', 'https://static.tildacdn.com/tild6637-3833-4334-b235-373164326332/user-female-circle-f.png', 'Грозный', ['Стратегия', 'Партнёры'], '12+ лет в HR и операциях. Запускала RPO/MSP-направления и BI-отчётность по найму.'],
                ['Петимат Межиева', 'COO • Операции и SLA', 'https://static.tildacdn.com/tild6637-3833-4334-b235-373164326332/user-female-circle-f.png', 'Грозный', ['Операции', 'SLA'], 'Выстраивает процессы, прогноз hiring-потока и контроль SLA/качества.'],
                ['Хеда Абубакарова', 'Head of Talent • Сорсинг и интервью', 'https://static.tildacdn.com/tild6637-3833-4334-b235-373164326332/user-female-circle-f.png', 'Remote', ['Сорсинг', 'Обучение'], 'Развивает сорсинг, стандарты интервью и обучение команды рекрутёров.'],
              ].map(([n, r, ph, loc, skills, bio]) => (
                <Card key={n as string}>
                  <div className="flex items-center gap-4">
                    <img src={ph as string} alt={n as string} className="w-20 h-20 rounded-xl object-cover" />
                    <div>
                      <h3 className="text-lg font-semibold">{n as string}</h3>
                      <p className="text-sm text-gray-300">{r as string}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-200 mt-4">{bio as string}</p>
                  <div className="mt-3 flex gap-2 text-xs">
                    <span className="tag">{loc as string}</span>
                    {Array.isArray(skills) && skills.map(skill => (
                      <span key={skill} className="tag">{skill}</span>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </section>

          <section id="team">
            <div className="flex flex-wrap items-end gap-4 justify-between">
              <h2 className="text-3xl md:text-4xl font-bold">Вся команда</h2>
              <span className="text-sm text-gray-300">Сотрудников: {items.length}</span>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-5">
              <input value={q} onChange={e => setQ(e.target.value)} className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2" placeholder="Поиск по имени/скиллам…" />
              <select value={dept} onChange={e => setDept(e.target.value as any)} className="rounded-xl px-4 py-3 text-gray-900">
                <option value="">Отдел: все</option>
                <option value="recruiting">Рекрутинг</option>
                <option value="sourcing">Сорсинг</option>
                <option value="ops">Операции</option>
                <option value="account">Аккаунт/PM</option>
                <option value="marketing">Маркетинг</option>
                <option value="analytics">Аналитика</option>
              </select>
              <select value={loc} onChange={e => setLoc(e.target.value as any)} className="rounded-xl px-4 py-3 text-gray-900">
                <option value="">Локация: все</option>
                <option value="remote">Remote</option>
                <option value="moscow">Москва</option>
                <option value="spb">Санкт-Петербург</option>
              </select>
              <select value={sort} onChange={e => setSort(e.target.value as any)} className="rounded-xl px-4 py-3 text-gray-900">
                <option value="name">По имени (А-Я)</option>
                <option value="dept">По отделу</option>
              </select>
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {items.map(p => (
                <Card key={p.id}>
                  <div className="flex items-center gap-4">
                    <img src={p.photo} alt={p.name} className="w-20 h-20 rounded-xl object-cover" />
                    <div>
                      <h3 className="text-lg font-semibold">{p.name}</h3>
                      <p className="text-sm text-gray-300">{p.role}</p>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs">
                        <span className="tag">{labelDept(p.dept)}</span>
                        <span className="tag">{labelLoc(p.loc)}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-200 mt-4">{p.bio}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {p.tags.map(t => <span key={t} className="tag">{t}</span>)}
                  </div>
                </Card>
              ))}
            </div>
            {items.length === 0 && <p className="text-gray-300">Нет совпадений. Очистите фильтры.</p>}
          </section>

          <section id="culture">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Как мы работаем</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {[
                ['Прозрачно', 'SLA, метрики, честные статусы по кандидатам и задачам.'],
                ['Быстро', 'Короткие циклы, синки по делу, решения — в тот же день.'],
                ['С заботой', 'Наставники, план 30/60/90 и поддержка баланса.'],
              ].map(([t, d]) => (
                <Card key={t as string}><h3 className="text-xl font-semibold mb-2">{t as string}</h3><p className="text-sm text-gray-200">{d as string}</p></Card>
              ))}
            </div>
          </section>

          <section id="careers" className="scroll-mt-32 rounded-2xl p-6 flex flex-col md:flex-row md:items-center md:justify-between"
            style={{ background: 'var(--card)', backdropFilter: 'blur(8px)' }}>
            <div>
              <h3 className="text-2xl font-bold">Хотите к нам?</h3>
              <p className="text-sm text-gray-200 mt-1">Открытые роли и быстрая обратная связь в течение 24 часов.</p>
            </div>
            <a href="/about/career" className="btn btn-accent mt-4 md:mt-0">Перейти в «Карьера»</a>
          </section>
        </main>
      </div>
    </>
  );
}